import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Mail } from 'lucide-react';
import { getTextColorForBackground } from '@/lib/core/utils';
import { Textarea } from '@/components/ui/textarea';
import { PersonalizationToolbar } from './PersonalizationToolbar';

// Helper to replace message text in HTML for live preview
function replaceMessageInHtml(html: string, originalMessage: string, newMessage: string): string {
  if (!originalMessage || !html) return html;
  if (originalMessage === newMessage) return html;

  // Normalize for comparison (handle HTML entities and whitespace)
  const normalizeText = (text: string) => {
    return text
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/\s+/g, ' ')
      .trim();
  };

  const normalizedOriginal = normalizeText(originalMessage);

  // Try to find and replace in td elements with dir="ltr" (Canva structure)
  const tdPattern = /(<td[^>]*dir=["']ltr["'][^>]*>)([\s\S]*?)(<\/td>)/gi;

  let replaced = false;
  const result = html.replace(tdPattern, (match, openTag, content, closeTag) => {
    if (replaced) return match; // Only replace first match

    const normalizedContent = normalizeText(content);
    if (normalizedContent === normalizedOriginal || content.trim() === originalMessage.trim()) {
      replaced = true;
      // Preserve any <br> tags
      const hasBr = content.includes('<br');
      return `${openTag}${newMessage}${hasBr ? '<br>' : ''}${closeTag}`;
    }
    return match;
  });

  if (replaced) return result;

  // Fallback: try simple replacement
  if (html.includes(originalMessage)) {
    return html.replace(originalMessage, newMessage);
  }

  // Try with trimmed versions
  const trimmedOriginal = originalMessage.trim();
  if (html.includes(trimmedOriginal)) {
    return html.replace(trimmedOriginal, newMessage.trim());
  }

  return html;
}

interface EmailTemplatePreviewProps {
  configuration: {
    preset?: string;
    logoUrl?: string;
    logoPosition?: 'left' | 'center' | 'right';
    primaryColor?: string;
    fontFamily?: string;
    headerText?: string;
    mainMessage?: string;
    ctaText?: string;
    ctaLink?: string;
    buttonType?: 'review' | 'coupon' | 'loyalty' | 'custom' | 'none';
    selectedReviewId?: string;
    selectedCouponId?: string;
    selectedLoyaltyId?: string;
    footerText?: string;
    unsubscribeText?: string;
    html?: string;
    // Canva App import fields
    canvaHeaderImage?: string;  // URL to uploaded Canva design image
    designImage?: string;  // Legacy: Base64 PNG from Canva (deprecated)
    importSource?: 'canva' | 'canva_app' | 'custom';
    canvaBlocks?: Array<{
      id: string;
      type: 'editable_text' | 'cta_button';
      content?: string;
      originalContent?: string;
      label?: string;
      buttonType?: string;
      ctaUrl?: string;
    }>;
    blocks?: Array<{
      id: string;
      type: 'editable_text' | 'cta_button';
      content?: string;
      label?: string;
      buttonType?: string;
      ctaUrl?: string;
    }>;
  };
  mode?: 'desktop' | 'mobile' | 'compact' | 'card';
  isAdvancedMode?: boolean;
  getButtonUrl?: (config: any) => string;
  getButtonText?: (config: any) => string;
  shouldShowButton?: (config: any) => boolean;
  logoPreview?: string;
  isEditingMessage?: boolean;
  editedMainMessage?: string;
  editedHeaderText?: string;
  onMessageChange?: (value: string) => void;
}

const EmailTemplatePreview: React.FC<EmailTemplatePreviewProps> = ({
  configuration,
  mode = 'desktop',
  isAdvancedMode = false,
  getButtonUrl = () => '#',
  getButtonText = () => 'Button',
  shouldShowButton = (config) => config?.buttonType && config.buttonType !== 'none',
  logoPreview,
  isEditingMessage = false,
  editedMainMessage = '',
  editedHeaderText = '',
  onMessageChange = () => {}
}) => {
  const previewMode = mode;
  const isCompact = mode === 'compact';
  const isCard = mode === 'card';
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const manualHeightRef = useRef<number | null>(null);
  const [iframeHeight, setIframeHeight] = useState<number | null>(null);
  const [debouncedMessage, setDebouncedMessage] = useState(editedMainMessage);
  const [debouncedHeaderText, setDebouncedHeaderText] = useState(editedHeaderText);

  // Debounce the edited message for live preview updates (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedMessage(editedMainMessage);
    }, 300);
    return () => clearTimeout(timer);
  }, [editedMainMessage]);

  // Debounce the edited header text for live preview updates (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedHeaderText(editedHeaderText);
    }, 300);
    return () => clearTimeout(timer);
  }, [editedHeaderText]);

  // Auto-size iframe to content for card mode
  const measureIframeHeight = () => {
    if (iframeRef.current) {
      try {
        const doc = iframeRef.current.contentDocument;
        if (doc?.body) {
          // Get the full height of the content
          const height = doc.documentElement?.scrollHeight || doc.body.scrollHeight;
          if (height > 0) {
            setIframeHeight(height);
          }
        }
      } catch (e) {
        // Cross-origin issues, use default height
        console.warn('Could not measure iframe height:', e);
      }
    }
  };

  const handleIframeLoad = () => {
    // Always measure iframe height for HTML content
    measureIframeHeight();
    // Also measure after a short delay for images/fonts to load
    setTimeout(measureIframeHeight, 100);
    setTimeout(measureIframeHeight, 500);
  };

  // Re-measure when configuration changes
  useEffect(() => {
    if (configuration?.html) {
      setIframeHeight(null); // Reset height to remeasure
    }
  }, [configuration?.html]);

  // Remove max-width constraints from HTML for previews
  const getCardHtml = (html: string) => {
    if (!html) return html;

    // Always remove max-width from inline styles for Canva templates (handles various formats)
    let modified = html.replace(/max-width\s*:\s*\d+\s*(px|em|rem|%)?/gi, 'max-width:100%');

    // Remove fixed width constraints too
    modified = modified.replace(/width\s*:\s*\d+\s*(px|em|rem)(?=[;"])/gi, 'width:100%');

    // Only inject height reporting for card mode
    if (!isCard) {
      // Just add margin/padding reset for non-card modes
      const basicStyle = `<style>html,body{margin:0!important;padding:0!important;}</style>`;
      if (modified.includes('</head>')) {
        return modified.replace('</head>', `${basicStyle}</head>`);
      } else if (modified.includes('<body')) {
        return modified.replace('<body', `${basicStyle}<body`);
      }
      return basicStyle + modified;
    }

    // Inject styles and script to report height to parent (card mode only)
    const injection = `<style>html,body{margin:0!important;padding:0!important;}</style>
<script>
function reportHeight(){
  var h = document.documentElement.scrollHeight || document.body.scrollHeight;
  parent.postMessage({type:'iframeHeight',height:h},'*');
}
window.onload = function(){reportHeight();setTimeout(reportHeight,100);setTimeout(reportHeight,500);};
document.addEventListener('DOMContentLoaded',reportHeight);
new MutationObserver(reportHeight).observe(document.body,{childList:true,subtree:true});
</script>`;

    if (modified.includes('</body>')) {
      modified = modified.replace('</body>', injection + '</body>');
    } else if (modified.includes('</html>')) {
      modified = modified.replace('</html>', injection + '</html>');
    } else {
      modified = modified + injection;
    }

    return modified;
  };

  // Get preview HTML with edited message applied (for live preview in iframe)
  const getPreviewHtml = useMemo(() => {
    if (!configuration?.html) return '';

    let html = configuration.html;

    // Find the original header text from canvaBlocks (if available)
    const headerTextBlock = configuration.canvaBlocks?.find(
      b => b.type === 'editable_text' && b.label === 'Header Text'
    );
    const originalHeaderText = headerTextBlock?.originalContent || headerTextBlock?.content;

    if (originalHeaderText) {
      // When editing, use the debounced header text for smoother updates
      // When not editing, use configuration.headerText (which has the saved edited value)
      const headerToShow = isEditingMessage ? debouncedHeaderText : configuration.headerText;

      if (headerToShow && headerToShow !== originalHeaderText) {
        html = replaceMessageInHtml(html, originalHeaderText, headerToShow);
      }
    }

    // Find the original message from canvaBlocks (if available)
    const mainMessageBlock = configuration.canvaBlocks?.find(
      b => b.type === 'editable_text' && b.label === 'Main Message'
    );
    const originalMessage = mainMessageBlock?.originalContent || mainMessageBlock?.content;

    if (originalMessage) {
      // When editing, use the debounced message for smoother updates
      // When not editing, use configuration.mainMessage (which has the saved edited value)
      const messageToShow = isEditingMessage ? debouncedMessage : configuration.mainMessage;

      if (messageToShow && messageToShow !== originalMessage) {
        html = replaceMessageInHtml(html, originalMessage, messageToShow);
      }
    }

    return html;
  }, [configuration?.html, configuration?.canvaBlocks, configuration?.mainMessage, configuration?.headerText, isEditingMessage, debouncedMessage, debouncedHeaderText]);

  // Focus and select all text in textarea when editing starts
  useEffect(() => {
    if (isEditingMessage && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
      // Reset manual height when starting to edit
      manualHeightRef.current = null;
    }
  }, [isEditingMessage]);

  // Auto-expand textarea based on content
  useEffect(() => {
    if (isEditingMessage && textareaRef.current) {
      const textarea = textareaRef.current;

      // Calculate the minimum height needed for content
      textarea.style.height = 'auto';
      const contentHeight = textarea.scrollHeight;

      // Set min-height to prevent resizing smaller than content
      textarea.style.minHeight = `${contentHeight}px`;

      // Determine final height
      let finalHeight = contentHeight;

      if (manualHeightRef.current !== null) {
        // User has manually resized - use the larger of manual height or content height
        finalHeight = Math.max(manualHeightRef.current, contentHeight);
      }

      textarea.style.height = `${finalHeight}px`;
    }
  }, [isEditingMessage, editedMainMessage]);

  // For compact mode, use smaller padding and font sizes
  const padding = isCompact ? 'p-4' : (previewMode === 'mobile' ? 'p-4' : 'p-6');
  const headerPadding = isCompact ? 'p-3 pb-2' : (previewMode === 'mobile' ? 'p-4 pb-2' : 'p-6 pb-3');
  const logoHeight = isCompact ? 'h-8' : (previewMode === 'mobile' ? 'h-10' : 'h-12');
  const titleSize = isCompact ? 'text-base' : (previewMode === 'mobile' ? 'text-xl' : 'text-2xl');
  const textSize = isCompact ? 'text-sm' : (previewMode === 'mobile' ? 'text-sm' : 'text-base');
  const buttonPadding = isCompact ? 'px-4 py-2 text-sm' : (previewMode === 'mobile' ? 'px-4 py-2 text-sm' : 'px-6 py-3');
  const footerPadding = isCompact ? 'px-4 py-2' : (previewMode === 'mobile' ? 'px-4 py-3' : 'px-6 py-4');
  const footerTextSize = isCompact ? 'text-xs' : (previewMode === 'mobile' ? 'text-xs' : 'text-sm');

  return (
    <div
      className={`transition-all duration-300 ${
        isAdvancedMode
          ? 'w-full overflow-hidden overflow-y-auto'
          : isCard
            ? 'w-full' // No overflow, no border, no padding for card mode
            : `${isCompact ? 'border-t' : 'border rounded-lg pt-4'} bg-white overflow-hidden overflow-y-auto ${
                previewMode === 'mobile' ? 'max-w-sm w-full' : 'w-full'
              }`
      }`}
      style={isAdvancedMode || isCard ? {} : {
        fontFamily: configuration?.fontFamily || 'Arial, sans-serif'
      }}
    >
      {/* Canva App HTML template preview */}
      {/* Use iframe for Canva imports or pure HTML templates (no preset) */}
      {configuration?.importSource === 'canva_app' && configuration?.html ? (
        <iframe
            ref={iframeRef}
            className="w-full border-0 block"
            scrolling={isCard ? "no" : "auto"}
            style={{
              height: isCard
                ? (iframeHeight ? `${iframeHeight}px` : '600px')
                : '600px',
              maxHeight: isCard ? undefined : '70vh',
              minHeight: '200px',
              display: 'block'
            }}
            srcDoc={getCardHtml(getPreviewHtml || configuration.html)}
            title="Canva Email Preview"
            onLoad={handleIframeLoad}
          />
      ) : (isAdvancedMode && !configuration?.preset) || (configuration?.html && !configuration?.importSource && !configuration?.preset) || (configuration?.importSource === 'canva' && configuration?.html) ? (
        <iframe
          ref={iframeRef}
          className={`w-full border-0 ${isCard ? 'block' : 'rounded-lg'}`}
          scrolling={isCard ? "no" : "auto"}
          style={{
            height: isCard
              ? (iframeHeight ? `${iframeHeight}px` : '600px')
              : '600px',
            maxHeight: isCard ? undefined : '70vh',
            minHeight: '200px',
            display: 'block'
          }}
          srcDoc={getCardHtml(getPreviewHtml || configuration?.html || '<div style="padding: 2rem; text-align: center; color: #666;">Enter HTML to see preview</div>')}
          title="Email Preview"
          onLoad={handleIframeLoad}
        />
      ) : (
        <div className="w-full bg-white">
          <div className="w-full">
            {/* Canva Design Header Image (new format - URL from storage) */}
            {configuration?.canvaHeaderImage && (
              <div className="w-full">
                <img
                  src={configuration.canvaHeaderImage}
                  alt="Email Design"
                  className="w-full h-auto"
                  style={{ maxHeight: isCompact ? '200px' : '400px', objectFit: 'contain' }}
                />
              </div>
            )}

            {/* Header with Logo (only show if no Canva header image) */}
            {!configuration?.canvaHeaderImage && (logoPreview || configuration?.logoUrl) && (
              <div
                className={`${headerPadding} ${
                  configuration?.logoPosition === 'left' ? 'text-left' :
                  configuration?.logoPosition === 'right' ? 'text-right' :
                  'text-center'
                }`}
              >
                <img
                  src={logoPreview || configuration?.logoUrl}
                  alt="Logo"
                  className={`object-contain inline-block ${logoHeight}`}
                />
              </div>
            )}

            {/* Main Content */}
            <div className={padding}>
              {/* Only show header text if no Canva header image (header is in the image) */}
              {!configuration?.canvaHeaderImage && (
                <h1 className={`font-bold ${isCompact ? 'mb-2' : 'mb-4'} text-gray-900 ${titleSize}`}>
                  {configuration?.headerText || 'Your Email Subject Here'}
                </h1>
              )}

              <div className={`text-gray-700 ${isCompact ? 'mb-3' : 'mb-6'} leading-relaxed ${textSize}`}>
                {isEditingMessage ? (
                  <>
                    <PersonalizationToolbar
                      textareaRef={textareaRef}
                      value={editedMainMessage}
                      onInsert={onMessageChange}
                    />
                    <Textarea
                      ref={textareaRef}
                      value={editedMainMessage}
                      onChange={(e) => onMessageChange(e.target.value)}
                      onMouseUp={() => {
                        // Save the height when user finishes dragging resize handle
                        if (textareaRef.current) {
                          manualHeightRef.current = textareaRef.current.offsetHeight;
                        }
                      }}
                      className={`w-full !bg-transparent border-gray-300 focus:border-gray-400 ${textSize} p-0 resize-y overflow-hidden leading-relaxed`}
                      placeholder="Enter your email message..."
                      style={{
                        fontFamily: configuration?.fontFamily || 'Arial, sans-serif',
                        fontSize: 'inherit',
                        color: '#374151', // text-gray-700 - force this color regardless of theme
                        background: 'transparent', // Force transparent background
                        minHeight: '3.25rem' // Minimum height for better UX
                      }}
                    />
                  </>
                ) : (
                  <div className="whitespace-pre-wrap">
                    {configuration?.mainMessage || 'Your email message content will appear here.'}
                  </div>
                )}
              </div>

              {shouldShowButton(configuration) && (
                <div className={isCompact ? 'mb-3' : 'mb-6'}>
                  <a
                    href={getButtonUrl(configuration)}
                    className={`inline-block font-semibold rounded-lg text-decoration-none hover:opacity-90 transition-opacity ${buttonPadding}`}
                    style={{
                      backgroundColor: configuration?.primaryColor || '#1976d2',
                      color: getTextColorForBackground(configuration?.primaryColor || '#1976d2')
                    }}
                  >
                    {getButtonText(configuration)}
                  </a>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className={`bg-gray-50 border-t text-center ${footerPadding}`}>
              <p className={`text-gray-600 ${footerTextSize}`}>
                {configuration?.footerText || 'Your business footer information here'}
                {configuration?.footerText && (
                  <>
                    <br />
                    <br />
                  </>
                )}
                <span className="text-gray-500 underline cursor-pointer">
                  {configuration?.unsubscribeText || 'Unsubscribe'}
                </span>
              </p>
            </div>

            {/* Show placeholder content if nothing is filled */}
            {!configuration?.headerText &&
             !configuration?.mainMessage &&
             !shouldShowButton(configuration) &&
             !configuration?.footerText &&
             !configuration?.canvaHeaderImage &&
             !(logoPreview || configuration?.logoUrl) && (
              <div className="p-12 text-center text-gray-500">
                <Mail className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium mb-2">Email Preview</p>
                <p className="text-sm">Fill in the template fields to see your email preview</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailTemplatePreview;
